import React, { useMemo } from "react";
import { AlertCircle, ArrowRight, Cake, Heart, Megaphone, Star, Sparkles, Ticket, CalendarDays } from "lucide-react";
import { Lien } from "./Lien";
import { Groupe, Ligne, TitreGroupe } from "./ui";
import { useReglages } from "../lib/useReglages";
import { construireAgenda, aVenir, periodeLisible } from "../lib/agenda";
import { SPECTACLES } from "../pages/Spectacles";

// Le lien « Écrire un avis » de la fiche Google de l'école (même identifiant que les avis
// affichés sur le site).
const AVIS_GOOGLE =
  "https://search.google.com/local/writereview?placeid=ChIJIW9wZUufBmMRvzwZ1Zud6QY";

/* Ce que CETTE famille doit faire : c'est ce qui passe avant toute autre information. */
export const AFaire = ({ moi, onOnglet }) => {
  const sansAvs = (moi.enfants || []).filter((e) => e.cours.length && !String(e.avs || "").trim());
  const lignes = [];
  if (moi.a_payer > 0.5) {
    // La date des derniers paiements enregistrés : « déjà payé ? » se lit tout de suite.
    const aJour = moi.paiements_a_jour_le
      ? ` (paiements enregistrés jusqu'au ${String(moi.paiements_a_jour_le).slice(0, 10).split("-").reverse().join(".")} : si tu as payé depuis, pas d'inquiétude)`
      : "";
    lignes.push({ id: "facture", texte: `Une facture attend ton paiement : CHF ${Number(moi.a_payer).toFixed(2)}${aJour}`,
      action: "Voir la facture", onglet: "factures" });
  }
  sansAvs.forEach((e) => lignes.push({
    id: `avs-${e.id}`,
    texte: `Numéro AVS de ${e.prenom} manquant : il est nécessaire pour l'attestation d'assurance.`,
    action: "Compléter", onglet: "cours",
  }));
  if (!lignes.length) return null;
  return (
    <div className="card-flat" data-testid="espace-a-faire"
      style={{ background: "#FFF7ED", borderColor: "#FED7AA" }}>
      <div className="eyebrow mb-2 flex items-center gap-1.5" style={{ color: "#C2410C" }}>
        <AlertCircle size={13} /> À faire
      </div>
      <ul className="space-y-2">
        {lignes.map((l) => (
          <li key={l.id} className="text-sm" data-testid={`espace-a-faire-${l.id}`}>
            <div>{l.texte}</div>
            <button onClick={() => onOnglet(l.onglet)} className="text-xs font-semibold underline mt-1"
              style={{ color: "#C2410C" }}>{l.action} →</button>
          </li>
        ))}
      </ul>
    </div>
  );
};

/* Les messages de l'école, écrits dans la console (Messages → Annonces de l'application). */
export const Annonces = ({ liste }) => {
  if (!liste?.length) return null;
  return (
    <div className="space-y-3" data-testid="espace-annonces">
      {liste.map((a) => (
        <div key={a.id} className="card-flat" style={{ borderColor: "var(--primary)" }}
          data-testid="espace-annonce">
          <div className="eyebrow mb-1 flex items-center gap-1.5"><Megaphone size={13} /> L'école vous informe</div>
          <div className="text-sm font-semibold">{a.titre}</div>
          {a.texte && <p className="text-sm text-[var(--muted)] mt-1 whitespace-pre-line">{a.texte}</p>}
          {a.lien && (
            <Lien href={a.lien} className="btn-primary text-xs mt-3 inline-flex">
              {a.lien_label || "En savoir plus"} <ArrowRight size={13} />
            </Lien>
          )}
        </div>
      ))}
    </div>
  );
};

/* À l'école prochainement (spectacles, stages) puis les services de 1000 ARTS. Les dates
   viennent du même agenda que la page « Dates importantes » du site. */
export const AVenirEcole = () => {
  const { portes_ouvertes: po } = useReglages();
  const prochains = useMemo(
    () => aVenir(construireAgenda(po, SPECTACLES))
      // Les spectacles ont leur section « Nos spectacles » sur l'Accueil (31.10.2026).
      .filter((e) => ["stage", "portes_ouvertes", "audition"].includes(e.type))
      .slice(0, 3),
    [po]);

  return (
    <div className="space-y-6" data-testid="espace-a-venir">
      <section>
        <TitreGroupe>Bientôt à l'école</TitreGroupe>
        <Groupe>
          {prochains.map((e) => (
            <Ligne key={e.id} Icone={e.type === "spectacle" ? Ticket : Sparkles}
              couleur={e.type === "spectacle" ? "#9333EA" : "#F59E0B"}
              titre={e.titre} sousTitre={periodeLisible(e.debut, e.fin)}
              href={e.lien?.to || e.lien?.href || "/dates-importantes"} testId={`espace-a-venir-${e.id}`} />
          ))}
          <Ligne Icone={CalendarDays} couleur="#0EA5E9" titre="Toutes les dates de la saison"
            sousTitre="Vacances, spectacles, stages" href="/dates-importantes"
            testId="espace-dates-importantes" />
        </Groupe>
      </section>

      <section>
        <TitreGroupe>Fêter avec 1000 ARTS</TitreGroupe>
        <Groupe>
          <Ligne Icone={Cake} couleur="#EC4899" titre="Fêter son anniversaire"
            sousTitre="Une fête dansée avec ses amis, animée par un prof, dans nos salles"
            href="/evenements/anniversaires" testId="espace-promo-anniversaire" />
          <Ligne Icone={Heart} couleur="#DC2626" titre="Danse de mariage"
            sousTitre="Ouverture de bal ou flashmob : on prépare votre chorégraphie ensemble"
            href="/evenements/mariage" testId="espace-promo-mariage" />
          <Ligne Icone={Star} couleur="#F59E0B" titre="Donner mon avis sur Google"
            sousTitre="Quelques mots aident d'autres familles à nous trouver (compte Google nécessaire)"
            href={AVIS_GOOGLE} testId="espace-avis-google" />
        </Groupe>
      </section>
    </div>
  );
};
